import { useEffect, useMemo } from "react";
import { useSite } from "@/lib/site";
import { useLocation } from "react-router-dom";

// SEO head manager with dynamic meta + JSON-LD injection.
// Note: this is CSR — for full SSR/SSG we'd need a Next.js migration.
export default function SEO({ slug, article, breadcrumb, faqs, service, canonicalPath, titleOverride, descriptionOverride, imageOverride }) {
  const { loadPageMeta, site } = useSite();
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    (async () => {
      const m = slug ? await loadPageMeta(slug).catch(() => null) : null;
      if (!alive) return;
      const origin = window.location.origin;
      // Use window.location.pathname instead of useLocation().pathname to preserve the public URL basename.
      const path = canonicalPath || window.location.pathname;
      const canonical = origin + (path === "/" ? "" : path);
      const title = titleOverride || m?.title || document.title;
      const description = descriptionOverride || m?.description || (site?.seo_defaults?.description ?? "");
      const ogImage = imageOverride || m?.og_image || site?.seo_defaults?.og_image || "";

      if (title) document.title = title;

      // helper
      const setMeta = (key, val, attr = "name") => {
        if (!val) return;
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute("content", val);
      };
      setMeta("description", description);
      if (m?.keywords) setMeta("keywords", m.keywords);
      setMeta("og:site_name", site?.seo_defaults?.site_name || "Miaki", "property");
      setMeta("og:type", article ? "article" : "website", "property");
      setMeta("og:title", title, "property");
      setMeta("og:description", description, "property");
      setMeta("og:url", canonical, "property");
      if (ogImage) setMeta("og:image", ogImage, "property");
      setMeta("twitter:card", m?.twitter_card || "summary_large_image");
      setMeta("twitter:title", title);
      setMeta("twitter:description", description);
      if (ogImage) setMeta("twitter:image", ogImage);
      if (site?.seo_defaults?.twitter_handle) setMeta("twitter:site", site.seo_defaults.twitter_handle);

      // canonical
      let can = document.querySelector('link[rel="canonical"]');
      if (!can) {
        can = document.createElement("link");
        can.setAttribute("rel", "canonical");
        document.head.appendChild(can);
      }
      can.setAttribute("href", canonical);
    })();
    return () => {
      alive = false;
    };
  }, [slug, canonicalPath, location.pathname, loadPageMeta, site, article, titleOverride, descriptionOverride, imageOverride]);

  // JSON-LD blobs
  const jsonld = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const blobs = [];
    blobs.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": origin + "/#organization",
      name: site?.brand?.name || "Miaki",
      url: origin,
      logo: origin + "/favicon.ico",
      email: site?.contact?.email,
      telephone: site?.contact?.phone,
      address: (site?.offices || []).map((o) => ({
        "@type": "PostalAddress",
        addressLocality: o.city,
        addressCountry: o.country,
        streetAddress: o.address,
      })),
      sameAs: [site?.social?.linkedin, site?.social?.facebook, site?.social?.github].filter(Boolean),
    });
    blobs.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": origin + "/#website",
      url: origin,
      name: site?.brand?.name || "Miaki",
      potentialAction: {
        "@type": "SearchAction",
        target: origin + "/insights?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    });
    if (breadcrumb && breadcrumb.length) {
      blobs.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumb.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: origin + b.path,
        })),
      });
    }
    if (article) {
      blobs.push({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        image: article.image,
        datePublished: article.date,
        author: { "@type": "Person", name: article.author || "Miaki" },
        publisher: { "@type": "Organization", name: "Miaki", logo: { "@type": "ImageObject", url: origin + "/favicon.ico" } },
        description: article.description,
        mainEntityOfPage: { "@type": "WebPage", "@id": origin + (canonicalPath || "") },
      });
    }
    if (service) {
      blobs.push({
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: service.type,
        provider: { "@type": "Organization", name: "Miaki" },
        areaServed: service.areaServed || "Global",
        description: service.description,
      });
    }
    if (faqs && faqs.length) {
      blobs.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    return blobs;
  }, [site, article, service, faqs, breadcrumb, canonicalPath]);

  useEffect(() => {
    // Remove old blobs then insert current
    document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    for (const b of jsonld) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(b);
      document.head.appendChild(s);
    }
  }, [jsonld]);

  return null;
}
